import React, { useRef, useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import { ChatTheme } from '@/types/theme';

/**
 * Props for InputArea component
 * Following IntentDrive.ai TypeScript standards
 */
interface InputAreaProps {
  message: string;
  setMessage: (message: string) => void;
  handleSendMessage: () => void;
  isDisabled?: boolean;
  theme?: ChatTheme;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  hasOptions?: boolean;
}

/**
 * Message input area with responsive design and accessibility
 * Follows IntentDrive.ai component patterns
 */
export function InputArea({
  message,
  setMessage,
  handleSendMessage,
  isDisabled = false,
  theme,
  placeholder = 'Type your message...',
  className = '',
  autoFocus = true,
  hasOptions = false,
}: InputAreaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Auto resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to calculate proper scroll height
    textarea.style.height = 'auto';

    // Set height based on scroll height (with max height)
    const newHeight = Math.min(textarea.scrollHeight, 120);
    textarea.style.height = `${newHeight}px`;
  }, [message]);

  // Auto focus on mount if specified
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // Mobile-specific fixes
  useEffect(() => {
    const handleFocus = () => {
      // On iOS, scroll to ensure input is visible
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        setTimeout(() => {
          window.scrollTo(0, document.body.scrollHeight);
        }, 100);
      }
    };

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('focus', handleFocus);
      return () => {
        textarea.removeEventListener('focus', handleFocus);
      };
    }
  }, []);

  // Handle key presses, particularly Enter key
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without shift for new line)
    if (e.key === 'Enter' && !e.shiftKey && !isDisabled && !hasOptions) {
      e.preventDefault();
      if (message.trim()) {
        handleSendMessage();
      }
    }
  };

  // Don't disable the input itself, only disable send functionality
  const shouldDisableInput = hasOptions;
  const shouldDisableSend = isDisabled || hasOptions || !message.trim();

  // Add timeout to re-enable send button if it's been disabled too long
  useEffect(() => {
    if (isDisabled) {
      const timeoutId = setTimeout(() => {
        console.log('⚠️ Send button has been disabled for 15 seconds, this might indicate a streaming issue');
      }, 15000);

      return () => clearTimeout(timeoutId);
    }
  }, [isDisabled]);

  return (
    <div
      className={`input-area ${className} ${isFocused ? 'focused' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'flex-end', // Align with bottom of potentially multi-line textarea
        padding: '0.75rem 1rem',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        backgroundColor: theme?.inputbackgroundcolor || '#FFFFFF',
        borderBottomLeftRadius: '0.75rem',
        borderBottomRightRadius: '0.75rem',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.03)'
      }}
    >
      <textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyPress}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={hasOptions ? "Please select an option above..." : placeholder}
        disabled={shouldDisableInput}
        aria-label="Message input"
        rows={1}
        style={{
          flex: 1,
          resize: 'none',
          border: `1px solid ${theme?.inputbordercolor || 'rgba(0, 0, 0, 0.08)'}`,
          borderRadius: '1.5rem',
          padding: '0.75rem 1rem',
          backgroundColor: theme?.inputbackgroundcolor || '#F5F7FB',
          color: theme?.bottextcolor || '#000000',
          lineHeight: '1.5',
          minHeight: '2.5rem',
          maxHeight: '120px',
          overflow: 'auto',
          outline: isFocused ? `2px solid ${theme?.primarycolor || '#497CFD'}` : 'none',
          outlineOffset: '2px',
          fontSize: '0.95rem',
          fontFamily: 'inherit',
          opacity: shouldDisableInput ? 0.7 : 1,
          cursor: shouldDisableInput ? 'not-allowed' : 'text'
        }}
      />

      <button
        onClick={handleSendMessage}
        disabled={shouldDisableSend}
        aria-label="Send message"
        className="send-button"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '2.5rem',
          height: '2.5rem',
          marginLeft: '0.5rem',
          borderRadius: '50%',
          backgroundColor: theme?.primarycolor || '#497CFD',
          border: 'none',
          color: theme?.buttontextcolor || '#FFFFFF',
          cursor: shouldDisableSend ? 'not-allowed' : 'pointer',
          opacity: shouldDisableSend ? 0.5 : 1,
          transition: 'all 0.2s'
        }}
      >
        <Send size={18} />
      </button>
    </div>
  );
}
